import React from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import ZoomableImage from '../../components/shared/ZoomableImage';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm6_u3_x3_stetige';
const IMAGE_BASE = '/assets/drawable/';

type TextBlock = { heading: string; body: string };

const textBlocks: TextBlock[] = [
  {
    heading: 'Einleitung',
    body: 'Stetig regelnde Ventilantriebe mit 0-10V Ansteuerung ermöglichen eine präzise und stufenlose Anpassung des Ventilhubs. Sie sind der Standard für anspruchsvolle Regelaufgaben in der modernen Gebäudeautomation, um Komfort und Energieeffizienz zu maximieren.'
  },
  {
    heading: 'Grundlagen der 0-10V Ansteuerung',
    body: 'Das Steuersignal ist eine Gleichspannung zwischen 0 und 10 Volt:\n\n•   0V DC: Ventil fährt in die definierte Grundstellung (meist ZU, z.B. 0% Hub).\n•   10V DC: Ventil fährt in die entgegengesetzte Endlage (meist AUF, z.B. 100% Hub).\n•   Zwischenwerte (z.B. 5V DC): Ventil fährt proportional in eine Zwischenposition (z.B. 50% Hub).\n\nDiese präzise Positionierung erlaubt eine sehr genaue Modulation des Durchflusses von Heiz- oder Kühlmedien.'
  },
  {
    heading: 'Stellungsrückmeldung (Feedback)',
    body: 'Viele stetige Antriebe bieten eine aktive Stellungsrückmeldung, ebenfalls oft als 0-10V DC Signal (oder 2-10V DC). Dieser Ausgang (Klemme U, YR o.ä.) meldet die tatsächliche, gemessene Position des Antriebs an die Regelung (DDC/SPS) zurück.\n\nVorteile der Rückmeldung:\n•   Bestätigung der korrekten Ventilposition.\n•   Schnellere Fehlererkennung (z.B. klemmendes Ventil, Antriebsdefekt).\n•   Optimierung von Regelalgorithmen.\n•   Visualisierung der Anlagenzustände.'
  },
  {
    heading: 'Typischer Elektrischer Anschluss',
    body: 'Ein typischer Anschluss für einen 24V AC/DC Antrieb mit 0-10V Steuerung und Rückmeldung:\n\n•   Klemme 1 (G0): Systemneutral, Masse (-)\n•   Klemme 2 (G): Spannungsversorgung 24V AC/DC (+)\n•   Klemme 3 (Y): Steuersignal Eingang 0-10V DC (vom Regler)\n•   Klemme 4/5 (U): Stellungsrückmeldung Ausgang 0-10V DC (zum Regler)\n\nDie genauen Klemmenbezeichnungen können je nach Hersteller und Modell variieren. Immer das Datenblatt des spezifischen Antriebs beachten!'
  },
  {
    heading: 'Anwendung: Einzelraumregelung (ERR)',
    body: 'In der Einzelraumregelung wird die Temperatur jedes Raumes individuell geregelt. Ein Raumfühler erfasst die Ist-Temperatur. Ein Raumregler (oft Teil einer DDC) vergleicht diese mit dem Sollwert und sendet ein 0-10V Signal an den stetigen Antrieb des Heizkörper- oder Zonenventils. So wird der Heiz- oder Kühlwasserdurchfluss präzise an den aktuellen Bedarf des Raumes angepasst. Die Rückmeldung ermöglicht es, die tatsächliche Ventilöffnung zu überwachen.'
  },
  {
    heading: 'Fazit',
    body: 'Stetige 0-10V Antriebe sind die präziseste Lösung für hochwertige Regelaufgaben in der Gebäudetechnik und bieten maximale Energieeffizienz und Komfort.'
  }
];

const images = [
  { id: 'img_stetig_beispiel', label: 'Stetiger Antrieb', src: `${IMAGE_BASE}ventil_stetig.webp`, alt: 'Beispiel stetiger 0-10V Ventilantrieb' },
  { id: 'img_stetig_schaltplan', label: 'Schaltplan stetig', src: `${IMAGE_BASE}schaltplan_stetig.PNG`, alt: 'Schaltplan stetiger Antrieb' },
  { id: 'img_stetig_erhitzer', label: 'Erhitzer + Pumpe', src: `${IMAGE_BASE}schaltplan_erhitzer_pumpe.PNG`, alt: 'Schaltplan Erhitzer mit Pumpe' },
  { id: 'img_stetig_frost', label: 'Frostschutz', src: `${IMAGE_BASE}schaltplan_frost.PNG`, alt: 'Schaltplan Frostschutzschaltung' }
];

const Page: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM6, canUploadImagesM6 } = useAuth();
  const canEditTexts = canEditTextsM6;
  const canUploadImages = canUploadImagesM6;

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const mergedBlocks = React.useMemo(
    () => textBlocks.map((tb, i) => ({ ...tb, body: override?.texts?.[`block_${i}`] || tb.body })),
    [override]
  );

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const fullPlainText = React.useMemo(() => mergedBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [mergedBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  const handleImageReplace = React.useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild erfolgreich aktualisiert.');
      await refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      alert('Text gespeichert.');
      await refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const ttsButton = React.createElement(IconButton, { onClick: speaking ? stop : speak, 'aria-label': 'Text vorlesen', sx: { ml: 1 } }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '6.3.3 Stetige Ventilantriebe (0-10V)', actions: ttsButton },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        images.map(img => React.createElement(ZoomableImage, { key: img.id, src: imgSrc(img.id, img.src), alt: img.alt, label: img.label, onReplace: canUploadImages ? (file: File) => handleImageReplace(img.id, file) : undefined }))
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        mergedBlocks.map((block, i) => React.createElement(EditableTextBlock, { key: i, pageId: PAGE_ID, blockId: `block_${i}`, heading: block.heading, body: block.body, onSave: (newBody: string) => handleTextSave(`block_${i}`, newBody) })),
        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } }, React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m6_u3_x99_quiz') }, 'Zum Quiz'))
      )
    )
  );
};

export default Page;


